import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, SquareCheckBig, MapPin, RefreshCw } from "lucide-react";
import {
    createUserPreferences,
    getAllCurrencies,
    getAllTimezones,
    getUserPreferences,
    updateUserPreferences,
    useAppDispatch,
} from "../../../redux";
import { useSelector } from "react-redux";
import { useTimezoneSelect, allTimezones } from "react-timezone-select";
import Spinner from "../../shared/components/Spinner";
import { selectLoaderValue } from "../../../redux/Slices/loadingSlice";
import Button from "../../../components/ui/Button";
import Card from "../../../components/ui/card";
import TimezoneDropdown from "../../../components/ui/TimezoneDropdown";
import CurrencyDropdown from "../../../components/ui/CurrencyDropdown";
import { 
    useCurrencyDetection, 
    getCachedCurrencyPreference, 
    setCachedCurrencyPreference,
    clearCurrencyCache 
} from "../../../lib/currencyUtils";

const labelStyle = "original";
const timezones = {
    ...allTimezones,
    "Europe/Berlin": "Frankfurt",
};
export default function PreferencesSetting() {
    const { t } = useTranslation();
    const isLoading = useSelector(selectLoaderValue);

    // State
    const [formData, setFormData] = useState({
        projectTypes: [],
        expertise: [],
        industries: [],
        budget: {
            minimum: 0,
            maximum: 0,
            currency: "",
        },
        availabilityHours: {
            start: "",
            end: "",
            timezone: "",
        },
    });
    // const { options, parseTimezone } = useTimezoneSelect({ labelStyle, timezones  });

    const { user, token, currencies, timezones } = useSelector((state: any) => state.auth);
    const { userPreferences } = useSelector((state: any) => state.client);

    const dispatch = useAppDispatch();
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isDetectingCurrency, setIsDetectingCurrency] = useState(false);
    const [detectedLocation, setDetectedLocation] = useState<string | null>(null);

    // Helper function to map currency data to CurrencyDropdown format
    const mapCurrencyForDropdown = (currencyId: string) => {
        if (!currencyId || !currencies) return null;
        const foundCurrency = currencies.find((curr: any) => curr.value === currencyId);
        if (!foundCurrency) return null;
        return {
            id: foundCurrency.value,
            name: foundCurrency.label,
            code: foundCurrency.initial || foundCurrency.value
        };
    };

    // Validation
    const validate = (formData: any) => {
        const errors: { [key: string]: string } = {};

        if (formData.projectTypes.length === 0 || formData.projectTypes.every((type: string) => type.trim() === "")) {
            errors.projectTypes = t("preferencesSettings.validation.projectTypesRequired");
        }

        if (formData.expertise.length === 0 || formData.expertise.every((skill: string) => skill.trim() === "")) {
            errors.expertise = t("preferencesSettings.validation.expertiseRequired");
        }

        if (!formData.budget.minimum || formData.budget.minimum < 0) {
            errors.budgetMinimum = t("preferencesSettings.validation.budgetMinimumInvalid");
        }

        if (!formData.budget.maximum || formData.budget.maximum <= formData.budget.minimum) {
            errors.budgetMaximum = t("preferencesSettings.validation.budgetMaximumInvalid");
        }

        if (!formData.budget.currency) {
            errors.currency = t("preferencesSettings.validation.currencyRequired");
        }

        if (!formData.availabilityHours.start) {
            errors.startTime = t("preferencesSettings.validation.startTimeRequired");
        }

        if (!formData.availabilityHours.end) {
            errors.endTime = t("preferencesSettings.validation.endTimeRequired");
        }

        // Handle submit
        if (!formData.availabilityHours.timezone) {
            errors.timezone = t("preferencesSettings.validation.timezoneRequired");
        }

        return errors;
    };

    useEffect(() => {
        getPreferencesData();
    }, []);

    const getPreferencesData = async () => {
        if (userPreferences == null) {
            await dispatch(getAllCurrencies({ isShowLoader: false }));
            await dispatch(getAllTimezones({ isShowLoader: false }));
            await dispatch(getUserPreferences({ id: user.id }));
        }
    };
    useEffect(() => {
        if (userPreferences) {
            setFormData({
                projectTypes: userPreferences.projectTypes || [],
                expertise: userPreferences.expertise || [],
                industries: [], // If industries are included in your API, update accordingly
                budget: {
                    minimum: userPreferences.minBudget || 0,
                    maximum: userPreferences.maxBudget || 0,
                    currency: userPreferences?.currency?.id || "",
                },
                availabilityHours: {
                    start: userPreferences.startAt
                        ? new Date(userPreferences.startAt).toISOString().substr(11, 5)
                        : "09:00",
                    end: userPreferences.endAt ? new Date(userPreferences.endAt).toISOString().substr(11, 5) : "18:00",
                    timezone: userPreferences?.timezone?.id || "",
                },
            });
        }
    }, [userPreferences]);

    // Currency detection function
    const detectAndSetCurrency = async () => {
        if (!currencies || currencies.length === 0) return;

        setIsDetectingCurrency(true);
        try {
            const detectedCurrencyId = await useCurrencyDetection(
                currencies,
                (currencyId, source) => {
                    console.log(`Currency detected via ${source}:`, currencyId);
                    setCachedCurrencyPreference(currencyId);
                    
                    // Update form data if no currency is set
                    if (!formData.budget.currency) {
                        handleNestedChange("budget", "currency", currencyId);
                    }
                    
                    // Set detected location info
                    if (source === "geolocation" || source === "ip") {
                        setDetectedLocation(`Detected via ${source}`);
                    }
                }
            );

            // If no currency is set in form, use detected one
            if (!formData.budget.currency) {
                handleNestedChange("budget", "currency", detectedCurrencyId);
            }
        } catch (error) {
            console.error("Error detecting currency:", error);
        } finally {
            setIsDetectingCurrency(false);
        }
    };

    // Auto-detect currency when currencies are loaded and no currency is set
    useEffect(() => {
        if (currencies && currencies.length > 0 && !formData.budget.currency) {
            // Check if we have a cached preference first
            const cachedCurrencyId = getCachedCurrencyPreference();
            if (cachedCurrencyId) {
                handleNestedChange("budget", "currency", cachedCurrencyId);
                setDetectedLocation("From saved preference");
            } else {
                // Auto-detect if no cached preference
                detectAndSetCurrency();
            }
        }
    }, [currencies, formData.budget.currency]);
    const handleSubmit = () => {
        const validationErrors = validate(formData);
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
            console.log("❌ Validation failed:", validationErrors);
            return;
        } else {
            const transformedData = {
                expertise: formData.expertise,
                projectTypes: formData.projectTypes,
                userId: { id: user?.id }, // Make sure `user` is defined from your auth state
                currency: { id: formData.budget.currency },
                timezone: { id: formData.availabilityHours.timezone },
                startAt: new Date(`1970-01-01T${formData.availabilityHours.start}:00Z`).toISOString(),
                endAt: new Date(`1970-01-01T${formData.availabilityHours.end}:00Z`).toISOString(),
                minBudget: formData.budget.minimum,
                maxBudget: formData.budget.maximum,
            };
            if (userPreferences != null) {
                dispatch(updateUserPreferences({ id: userPreferences.id, data: transformedData }));
            } else {
                dispatch(createUserPreferences({ data: transformedData }));
            }
        }
    };

    const handleChange = (field: string, value: any) => {
        const updatedFormData = {
            ...formData,
            [field]: value,
        };

        setFormData(updatedFormData);
        // setErrors(validate(updatedFormData)); // Re-run validation on change
    };

    const handleNestedChange = (parent: string, key: string, value: any) => {
        const updatedFormData = {
            ...formData,
            [parent]: {
                ...formData[parent],
                [key]: value,
            },
        };
        setFormData(updatedFormData);
        // setErrors(validate(updatedFormData)); // 🔁 Re-run validation
    };

    return (
        <>
            {isLoading && (
                <div className="absolute top-[50%] left-[50%]">
                    <Spinner color="gray" />
                </div>
            )}
            <Card color={undefined} className={`p-6 ${isLoading ? "opacity-25" : "opacity-100"}`}>
                <h2 className="text-xl font-semibold mb-6">{t("preferencesSettings.title")}</h2>

                {/* Project Types */}
                <div className="mb-6">
                    <label className="block text-sm font-medium mb-2">{t("preferencesSettings.projectTypes")}</label>
                    <div className="space-y-2">
                        {formData.projectTypes.map((type, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <input
                                    type="text"
                                    value={type}
                                    onChange={e => {
                                        const newTypes = [...formData.projectTypes];
                                        newTypes[index] = e.target.value;
                                        handleChange("projectTypes", newTypes);
                                    }}
                                    className="flex-1 p-2 border rounded-lg"
                                />
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => {
                                        const newTypes = formData.projectTypes.filter((_, i) => i !== index);
                                        handleChange("projectTypes", newTypes);
                                    }}>
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        ))}
                        <Button
                            variant="outline"
                            onClick={() => handleChange("projectTypes", [...formData.projectTypes, ""])}>
                            {t("preferencesSettings.addProjectType")}
                        </Button>
                        {errors.projectTypes && <p className="text-sm text-red-500">{errors.projectTypes}</p>}
                    </div>
                </div>

                {/* Expertise */}
                <div className="mb-6">
                    <label className="block text-sm font-medium mb-2">{t("preferencesSettings.expertise")}</label>
                    <div className="space-y-2">
                        {formData.expertise.map((skill, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <input
                                    type="text"
                                    value={skill}
                                    onChange={e => {
                                        const newSkills = [...formData.expertise];
                                        newSkills[index] = e.target.value;
                                        handleChange("expertise", newSkills);
                                    }}
                                    className="flex-1 p-2 border rounded-lg"
                                />
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => {
                                        const newSkills = formData.expertise.filter((_, i) => i !== index);
                                        handleChange("expertise", newSkills);
                                    }}>
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        ))}
                        <Button
                            variant="outline"
                            onClick={() => handleChange("expertise", [...formData.expertise, ""])}>
                            {t("preferencesSettings.addExpertise")}
                        </Button>
                        {errors.expertise && <p className="text-sm text-red-500">{errors.expertise}</p>}
                    </div>
                </div>

                {/* Budget */}
                <div className="mb-6">
                    <label className="block text-sm font-medium mb-2">{t("preferencesSettings.budget")}</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="text-sm text-gray-600 mb-1 block">{t("preferencesSettings.minimum")}</label>
                            <input
                                type="number"
                                value={formData.budget.minimum}
                                onChange={e => handleNestedChange("budget", "minimum", parseInt(e.target.value || "0"))}
                                className="w-full p-2 border rounded-lg"
                            />
                            {errors.budgetMinimum && <p className="text-sm text-red-500">{errors.budgetMinimum}</p>}
                        </div>
                        <div>
                            <label className="text-sm text-gray-600 mb-1 block">{t("preferencesSettings.maximum")}</label>
                            <input
                                type="number"
                                value={formData.budget.maximum}
                                onChange={e => handleNestedChange("budget", "maximum", parseInt(e.target.value || "0"))}
                                className="w-full p-2 border rounded-lg"
                            />
                            {errors.budgetMaximum && <p className="text-sm text-red-500">{errors.budgetMaximum}</p>}
                        </div>
                        <div>
                            <label className="text-sm text-gray-600 mb-1 block">
                                {t("preferencesSettings.currency")}
                                {isDetectingCurrency && (
                                    <span className="ml-2 text-xs text-blue-600">
                                        {t("preferencesSettings.detecting")}
                                    </span>
                                )}
                            </label>
                            <div className="relative">
                                <CurrencyDropdown
                                    selectedCurrency={mapCurrencyForDropdown(formData?.budget?.currency)}
                                    onCurrencySelect={(currency) => {
                                        handleNestedChange("budget", "currency", currency?.id || "");
                                        setCachedCurrencyPreference(currency?.id || "");
                                        setDetectedLocation("Manually selected");
                                    }}
                                    placeholder={t("preferencesSettings.selectCurrencyPlaceholder")}
                                    className="w-full"
                                    autoDetect={true}
                                    disabled={isDetectingCurrency}
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        clearCurrencyCache();
                                        detectAndSetCurrency();
                                    }}
                                    disabled={isDetectingCurrency}
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed z-10"
                                    title={t("preferencesSettings.detectCurrencyTitle")}
                                >
                                    <MapPin className="h-4 w-4 text-gray-500" />
                                </button>
                            </div>
                            {detectedLocation && (
                                <p className="text-xs text-gray-500 mt-1">
                                    {t("preferencesSettings.detectedLocation", { location: detectedLocation })}
                                </p>
                            )}
                            {errors.currency && <p className="text-sm text-red-500">{errors.currency}</p>}
                        </div>
                    </div>
                </div>

                {/* Availability Hours */}
                <div className="mb-6">
                    <label className="block text-sm font-medium mb-2">{t("preferencesSettings.availabilityHours")}</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="text-sm text-gray-600 mb-1 block">{t("preferencesSettings.startTime")}</label>
                            <input
                                type="time"
                                value={formData.availabilityHours.start}
                                onChange={e => handleNestedChange("availabilityHours", "start", e.target.value)}
                                className="w-full p-2 border rounded-lg"
                            />
                            {errors.startTime && <p className="text-sm text-red-500">{errors.startTime}</p>}
                        </div>
                        <div>
                            <label className="text-sm text-gray-600 mb-1 block">{t("preferencesSettings.endTime")}</label>
                            <input
                                type="time"
                                value={formData.availabilityHours.end}
                                onChange={e => handleNestedChange("availabilityHours", "end", e.target.value)}
                                className="w-full p-2 border rounded-lg"
                            />
                            {errors.endTime && <p className="text-sm text-red-500">{errors.endTime}</p>}
                        </div>
                        <div>
                            <label className="text-sm text-gray-600 mb-1 block">{t("preferencesSettings.timezone")}</label>
                            <TimezoneDropdown
                                selectedTimezone={timezones?.find(tz => tz.id === formData?.availabilityHours?.timezone || tz.value === formData?.availabilityHours?.timezone)}
                                onTimezoneSelect={(timezone) => {
                                    handleNestedChange("availabilityHours", "timezone", timezone?.id || timezone?.value || "");
                                }}
                                placeholder={t("preferencesSettings.selectTimezonePlaceholder")}
                                className="w-full"
                                autoDetect={true}
                            />
                            {errors.timezone && <p className="text-sm text-red-500">{errors.timezone}</p>}
                        </div>
                    </div>
                </div>
            </Card>
            <div className="flex justify-end mt-4">
                {userPreferences == null ? (
                    <button
                        className={"px-4 py-2 text-white rounded-md flex items-center bg-blue-600 hover:bg-blue-700"}
                        onClick={handleSubmit}>
                        <SquareCheckBig className="h-5" />
                        <span className="pl-2">{t("preferencesSettings.saveChanges")}</span>
                    </button>
                ) : (
                    <button
                        className={"px-4 py-2 text-white rounded-md flex items-center bg-blue-600 hover:bg-blue-700"}
                        onClick={handleSubmit}>
                        <SquareCheckBig className="h-5" />
                        <span className="pl-2">{t("preferencesSettings.updateChanges")}</span>
                    </button>
                )}
            </div>
        </>
    );
}
